import React, { useCallback, useEffect, useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import CloseIcon from "@mui/icons-material/Close";
import { Country, State } from "country-state-city";
import Autocomplete from "@mui/material/Autocomplete";

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  IconButton,
  Chip,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from "@mui/material";
import ActionButton, {
  ActionButtonGroup,
} from "../../../Common/Component/ActionButton";
import useConfig from "../../../hooks/useConfig";
import { Delete } from "@mui/icons-material";
import HttpService from "../../../hooks/Https-services";
import PageRoutes, { APIRoutes } from "../../../utils/constants";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";

interface RulesTableProps {
  rules: any[];
  onEdit: (rule: any) => void;
  onDelete: (id: number) => void;
  onView: (id: number) => void;
  onToggleStatus: (rule: any) => void;
}

const RulesTable = ({
  rules,
  onEdit,
  onDelete,
  onView,
  onToggleStatus,
}: RulesTableProps) => {
  const configData = useConfig();

  return (
    <TableContainer component={Paper}>
      <Table size="medium">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "normal" }}>ID</TableCell>
            <TableCell sx={{ fontWeight: "normal" }}>Rule Name</TableCell>
            <TableCell sx={{ fontWeight: "normal" }}>Description</TableCell>
            <TableCell sx={{ fontWeight: "normal" }}>Type</TableCell>
            <TableCell sx={{ fontWeight: "normal" }}>
              Countries/States
            </TableCell>
            <TableCell sx={{ fontWeight: "normal" }} align="right">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rules.map((rule: any, index: number) => (
            <TableRow key={rule.ID}>
              <TableCell sx={{ textTransform: "none" }}>
                <Typography sx={{ fontWeight: "normal" }}>{rule.ID}</Typography>
              </TableCell>
              <TableCell sx={{ textTransform: "none" }}>
                <Typography sx={{ fontWeight: "normal" }}>
                  {rule.Title}
                </Typography>
              </TableCell>
              <TableCell sx={{ textTransform: "none" }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: "normal" }}
                >
                  {rule.Description ? rule.Description : "-"}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={rule.Type.split("_")?.join(" ")}
                  size="small"
                  sx={{ textTransform: "none", fontWeight: "normal" }}
                  color={
                    rule.Type === "PERMIT_ALL"
                      ? "success"
                      : rule.Type === "RESTRICT_COUNTRIES_OR_STATES"
                      ? "error"
                      : "primary"
                  }
                />
              </TableCell>
              <TableCell sx={{ textTransform: "none" }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: "normal" }}
                >
                  {rule?.Countries?.length > 0 ||
                  rule?.States?.length > 0 ||
                  rule?.Cities?.length > 0 ? (
                    <>
                      {rule?.Countries?.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: "bold" }}
                          >
                            Countries:
                          </Typography>{" "}
                          {rule?.Countries?.filter((c: any) => c.Name)
                            .map((c: any) => c.Name)
                            .join(", ")}
                        </Box>
                      )}
                      {rule?.States?.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: "bold" }}
                          >
                            States:
                          </Typography>{" "}
                          {rule?.States?.filter((s: any) => s.Name)
                            .map((s: any) => `${s.Name} (${s.CountryCode})`)
                            .join(", ")}
                        </Box>
                      )}
                      {rule?.Cities?.length > 0 && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: "bold" }}
                          >
                            Cities:
                          </Typography>{" "}
                          {rule?.Cities?.filter((c: any) => c.Name)
                            .map(
                              (c: any) =>
                                `${c.Name} (${c.CountryCode}${
                                  c.StateCode ? ", " + c.StateCode : ""
                                })`
                            )
                            .join(", ")}
                        </Box>
                      )}
                    </>
                  ) : (
                    "All Countries/States/Cities"
                  )}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <ActionButtonGroup gap={1}>
                  <ActionButton
                    width="200px"
                    onClick={() => onToggleStatus(rule)}
                    className={configData.buttonCommonClass}
                  >
                    {rule.IsActive ? "Move to Pending" : "Make available"}
                  </ActionButton>

                  <ActionButton
                    width="50px"
                    onClick={() => onEdit(rule)}
                    className={configData.buttonCommonClass}
                  >
                    <EditIcon />
                  </ActionButton>
                  <ActionButton
                    width="50px"
                    onClick={() => onView(rule)}
                    className={configData.buttonCommonClass}
                  >
                    <RemoveRedEyeIcon />
                  </ActionButton>

                  <ActionButton
                    width="50px"
                    onClick={() => onDelete(rule.ID)}
                    className={configData.buttonCommonClass}
                  >
                    <Delete />
                  </ActionButton>
                </ActionButtonGroup>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Rule Manager Component
const RuleManager = ({
  rules,
  onUpdateRules,
  activeTab,
  setActiveTab,
}: any) => {
  const [open, setOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<any>(null);
  const configData = useConfig();
  const [countries, setCountries] = useState<any>([]);
  const [states, setStates] = useState<any>([]);
  const [cities, setCities] = useState<any>([]);
  const [formData, setFormData] = useState<any>({
    rule_name: "",
    rule_type: "PERMIT_ALL",
    rule_description: "",
    rule_countries: [],
    rule_states: [],
    rule_cities: [],
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setEditingRule(null);
    setFormData({
      rule_name: "",
      rule_type: "PERMIT_ALL",
      rule_description: "",
      rule_countries: [],
      rule_states: [],
      rule_cities: [],
    });
  };

  const createRule = async () => {
    try {
      await HttpService.post(APIRoutes.CreateRule, formData);
      onUpdateRules();
      handleClose();
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };

  const updateRule = async (id: number, rule: any) => {
    try {
      await HttpService.put(
        APIRoutes.UpdateRule.replace(":id", id.toString()),
        rule
      );
      onUpdateRules();
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = async () => {
    try {
      if (!formData.rule_name.trim()) return;
      const newRule = {
        ...formData,
        status: "active",
      };

      if (editingRule) {
        setIsLoading(true);

        await updateRule(editingRule.ID, newRule);
      } else {
        setIsLoading(true);
        await createRule();
      }
      setIsLoading(false);
      handleClose();
    } catch (error: any) {
      toast.error(error.response.data.message);
      setIsLoading(false);
    }
  };

  const handleEdit = (rule: any) => {
    const ruleDetails = {
      rule_countries: rule.Countries || [],
      rule_states: rule.States || [],
      rule_cities: rule.Cities || [],
      rule_type: rule.Type,
      rule_description: rule.Description,
      rule_name: rule.Title,
    };
    setEditingRule(rule);
    setFormData(ruleDetails);
    setOpen(true);
  };
  const navigate = useNavigate();
  const handleView = (rule: any) => {
    navigate(PageRoutes.RulesView.replace(":id", rule.ID));
  };

  const handleDelete = async (ruleId: any) => {
    try {
      await HttpService.put(APIRoutes.UpdateRule.replace(":id", ruleId), {
        is_deleted: true,
      });
      onUpdateRules();
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };
  const GetCountries = useCallback(async () => {
    try {
      // Load all countries
      const allCountries = Country.getAllCountries();
      setCountries(
        allCountries.map((country) => ({
          Code: country.isoCode,
          Name: country.name,
          Code2: country.isoCode,
        }))
      );

      // Load all states/provinces
      const allStates = State.getAllStates();
      setStates(
        allStates.map((state) => ({
          Code: state.isoCode,
          Name: state.name,
          CountryCode: state.countryCode,
        }))
      );

      // Load all cities
      // We'll create an array to store all cities from all countries
      const allCitiesArray: any[] = [];

      // For each country, get its cities and add to our array
      // allCountries.forEach((country) => {
      //   const countryCities = City.getCitiesOfCountry(country.isoCode) || [];

      //   // Map city data to our format and add country info
      //   const formattedCities = countryCities.map((city) => ({
      //     Code: city.name.replace(/\s+/g, "_"), // Use name as code with underscores
      //     Name: city.name,
      //     CountryCode: country.isoCode,
      //     CountryName: country.name,
      //     StateCode: city.stateCode,
      //   }));

      //   // Add to our main array
      //   allCitiesArray.push(...formattedCities);
      // });

      // Set cities state
      setCities(allCitiesArray);
    } catch (error: any) {
      toast.error(error.message || "Failed to load location data");
    }
  }, []);

  useEffect(() => {
    GetCountries();
  }, [GetCountries]);
  // Handle country selection in autocomplete
  const handleCountryChange = (event: any, newValue: any) => {
    // Update selected countries
    setFormData((prev: any) => ({
      ...prev,
      rule_countries: newValue,
    }));
  };

  // Handle state selection in autocomplete
  const handleStateChange = (event: any, newValue: any) => {
    // Update selected states
    setFormData((prev: any) => ({
      ...prev,
      rule_states: newValue,
    }));
  };

  // Handle city selection in autocomplete
  const handleCityChange = (event: any, newValue: any) => {
    // Update selected cities
    setFormData((prev: any) => ({
      ...prev,
      rule_cities: newValue,
    }));
  };

  // Get all states - we treat countries and states as separate entities
  const getFilteredStates = useCallback(() => {
    // Return all states regardless of country selection
    return states;
  }, [states]);

  // Get all cities - we treat countries, states, and cities as separate entities
  const getFilteredCities = useCallback(() => {
    // Return all cities regardless of country or state selection
    return cities;
  }, [cities]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", py: 2 }}>
          <Typography variant="h3"> Geoblocking Rules</Typography>
        </Box>
        <Box>
          <button
            onClick={handleOpen}
            style={{
              cursor: "pointer",
            }}
            className={configData.buttonCommonClass}
          >
            Add Rule
          </button>{" "}
        </Box>
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ color: "black" }}>
            {editingRule ? "Edit Rule" : "Add New Rule"}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Rule Name"
              value={formData.rule_name}
              onChange={(e) =>
                setFormData((prev: any) => ({
                  ...prev,
                  rule_name: e.target.value,
                }))
              }
              fullWidth
              required
            />

            <FormControl fullWidth>
              <InputLabel>Rule Type</InputLabel>
              <Select
                value={formData.rule_type}
                label="Rule Type"
                onChange={(e) => {
                  // Reset countries, states, and cities if changing rule type
                  if (e.target.value === "PERMIT_ALL") {
                    setFormData((prev: any) => ({
                      ...prev,
                      rule_type: e.target.value,
                      rule_countries: [],
                      rule_states: [],
                      rule_cities: [],
                    }));
                  } else {
                    setFormData((prev: any) => ({
                      ...prev,
                      rule_type: e.target.value,
                    }));
                  }
                }}
              >
                <MenuItem value="PERMIT_ALL">Permit All</MenuItem>
                <MenuItem value="RESTRICT_COUNTRIES_OR_STATES">
                  Restrict Countries/States
                </MenuItem>
                <MenuItem value="PERMIT_COUNTRIES_OR_STATES">
                  Permit Countries/States Only
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Description"
              value={formData.rule_description}
              onChange={(e) =>
                setFormData((prev: any) => ({
                  ...prev,
                  rule_description: e.target.value,
                }))
              }
              multiline
              rows={2}
              fullWidth
            />

            {formData.rule_type !== "PERMIT_ALL" && (
              <>
                {/* Country Selection with Autocomplete */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle1">Countries</Typography>
                    {formData.rule_countries.length > 0 && (
                      <Chip
                        size="small"
                        color="primary"
                        label={`${formData.rule_countries.length} selected`}
                      />
                    )}
                  </Box>
                  <Autocomplete
                    multiple
                    id="country-select"
                    options={countries}
                    value={formData.rule_countries}
                    getOptionLabel={(option) => option.Name}
                    onChange={handleCountryChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Select Countries"
                        placeholder="Search countries"
                      />
                    )}
                  />
                </FormControl>

                {/* State Selection with Autocomplete - Only shows states for selected countries */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle1">
                      States/Provinces
                    </Typography>
                    {formData.rule_states.length > 0 && (
                      <Chip
                        size="small"
                        color="secondary"
                        label={`${formData.rule_states.length} selected`}
                      />
                    )}
                  </Box>
                  <Autocomplete
                    multiple
                    id="state-select"
                    options={getFilteredStates()}
                    value={formData.rule_states}
                    getOptionLabel={(option) =>
                      `${option.Name} (${option.CountryCode})`
                    }
                    groupBy={(option) => option.CountryCode}
                    onChange={handleStateChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Select States/Provinces"
                        placeholder="Search states"
                      />
                    )}
                  />
                </FormControl>

                {/* City Selection with Autocomplete */}
                {/* <FormControl fullWidth sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle1">Cities</Typography>
                    {formData.rule_cities.length > 0 && (
                      <Chip
                        size="small"
                        color="info"
                        label={`${formData.rule_cities.length} selected`}
                      />
                    )}
                  </Box>
                  <Autocomplete
                    multiple
                    id="city-select"
                    options={getFilteredCities()}
                    value={formData.rule_cities}
                    getOptionLabel={(option) =>
                      `${option.Name} (${option.CountryName}${
                        option.StateCode ? ", " + option.StateCode : ""
                      })`
                    }
                    groupBy={(option) => option.CountryCode}
                    onChange={handleCityChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Select Cities"
                        placeholder="Search cities"
                      />
                    )}
                  />
                </FormControl> */}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <LoadingButton
            loading={isLoading}
            onClick={handleSubmit}
            variant="contained"
            color="primary"
          >
            {editingRule ? "Update" : "Create"} Rule
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Box sx={{ borderColor: "divider", mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_e: React.SyntheticEvent, newValue: string) =>
            setActiveTab(newValue)
          }
        >
          <Tab label="Active Rules" value="active" />
          <Tab label="Inactive Rules" value="inactive" />
        </Tabs>
      </Box>

      <RulesTable
        rules={rules}
        onEdit={handleEdit}
        onView={handleView}
        onDelete={handleDelete}
        onToggleStatus={(rule: any) => {
          updateRule(rule.ID, {
            is_active: rule.IsActive === true ? false : true,
          });
        }}
      />
    </Box>
  );
};

// Main App Component
const Geoblocking = () => {
  const [rules, setRules] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState("active");

  const getRules = useCallback(async () => {
    try {
      const query: any = {
        status: activeTab === "active" ? "true" : "false",
        page: 1,
        limit: 10,
      };
      const res = await HttpService.get(
        APIRoutes.GetRuleList + "?" + new URLSearchParams(query).toString()
      );
      setRules(res.data.data?.rules || []);
    } catch (error: any) {
      console.log(error);
    }
  }, [activeTab]);

  useEffect(() => {
    getRules();
  }, [getRules]);
  return (
    <div className="min-h-screen bg-gray-100">
      <RuleManager
        rules={rules}
        onUpdateRules={() => getRules()}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
    </div>
  );
};

export default Geoblocking;
